/**
 * Today — the super admin's plate.
 *
 * One list of everything to tend to across every customer and lead, grouped by
 * urgency (Overdue → Due today → Upcoming → No date), one row shape for every
 * kind of item, and the action right on the row. Reads GET /crm/today/ (the
 * server decides what counts and where it lands — see crm/services/today.py);
 * actions reuse the endpoints that already exist for each thing.
 *
 * The browser sends its own calendar day: the API runs on UTC and its "today"
 * would already be tomorrow every evening.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button, DatePicker, Empty, Input, Modal, Popconfirm, Segmented, Select, Tooltip, message,
} from "antd";
import {
  ArrowRightOutlined, CheckOutlined, CheckSquareOutlined, EyeInvisibleOutlined, FormOutlined,
  FunnelPlotOutlined, LinkOutlined, MailOutlined, MessageOutlined, PlusOutlined, ReloadOutlined,
  SmileOutlined, UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  addOfficeNote, dismissTodayItem, getOfficesList, getTaskAssignees, getToday, resendInvite,
  restoreTodayItem, reviewPlatformRequest, revokeInvite, updateOfficeNote,
} from "./controller";
import localDay from "./lib/localDay";
import "./today.css";

dayjs.extend(relativeTime);

const SCOPE_KEY = "crm.today.scope";
const KIND_ORDER = ["task", "invite", "thread", "lead", "request"];
const KIND = {
  task: { label: "Tasks", one: "task", Icon: CheckSquareOutlined },
  invite: { label: "Invites", one: "invite", Icon: MailOutlined },
  thread: { label: "Emails", one: "email", Icon: MessageOutlined },
  lead: { label: "Leads", one: "lead", Icon: FunnelPlotOutlined },
  request: { label: "Requests", one: "request", Icon: FormOutlined },
};
const SECTION_SUB = {
  overdue: "Past their moment",
  today: "",
  upcoming: "Everything scheduled ahead",
  unscheduled: "Open tasks without a date — never late",
};
const FOLD_UNSCHEDULED_AT = 10;

function readScope() {
  try {
    const v = localStorage.getItem(SCOPE_KEY);
    return v === "all" ? "all" : "me";
  } catch (e) {
    return "me";
  }
}
function writeScope(v) {
  try { localStorage.setItem(SCOPE_KEY, v); } catch (e) { /* private mode */ }
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

// What the "when" pill says, per kind (the server already bucketed against the caller's day).
function whenText(item) {
  const w = item.when || {};
  const f = item.flags || {};
  if (item.kind === "task") {
    if (w.bucket === "unscheduled") return "No date";
    if (w.bucket === "today") return "Today";
    if (w.bucket === "overdue") return `Overdue · ${plural(f.days || 0, "day")}`;
    return dayjs(w.date).format("ddd, MMM D");
  }
  if (item.kind === "invite") {
    const d = f.days_left;
    if (d === 0) return "Expires today";
    if (d === 1) return "Expires tomorrow";
    return `Expires in ${plural(d || 0, "day")}`;
  }
  if (item.kind === "thread") {
    const h = f.hours_waiting || 0;
    return h < 48 ? `No reply · ${h}h` : `No reply · ${plural(Math.floor(h / 24), "day")}`;
  }
  if (item.kind === "lead") return `Arrived ${plural(f.days_old || 0, "day")} ago`;
  if (item.kind === "request") return `Submitted ${plural(f.days_old || 0, "day")} ago`;
  return "";
}

function whenSub(item) {
  if (item.kind === "invite" && item.raw && item.raw.sent_at_estimate) {
    return `sent ~${dayjs(item.raw.sent_at_estimate).format("MMM D")}`;
  }
  if (item.kind === "task" && item.when && item.when.bucket === "overdue" && item.when.date) {
    return `due ${dayjs(item.when.date).format("MMM D")}`;
  }
  return null;
}

function Who({ item }) {
  const who = item.who;
  if (item.kind === "task") {
    if (!who) return <span className="crmt-who is-none">Unassigned</span>;
    return (
      <span className={`crmt-who${item.flags && item.flags.is_mine ? " is-me" : ""}`}>
        {item.flags && item.flags.is_mine ? "You" : who.name || who.email}
      </span>
    );
  }
  if (item.kind === "invite" && who) return <span className="crmt-who">by {who.name || who.email}</span>;
  if (item.kind === "thread" && who && who.email) return <span className="crmt-who" title={who.email}>{who.email}</span>;
  if (item.kind === "request" && who) return <span className="crmt-who">{who.name || who.email}</span>;
  return null;
}

function Chips({ item, onOpenOffice, onOpenLead }) {
  const ctx = item.context || {};
  const f = item.flags || {};
  const chips = [];
  if (ctx.office) {
    const label = ctx.practice && ctx.practice.name && ctx.practice.name !== ctx.office.name
      ? `${ctx.practice.name} › ${ctx.office.name}`
      : ctx.office.name || "Office";
    chips.push(
      <button
        key="office"
        type="button"
        className="crmt-chip"
        title={label}
        onClick={(e) => { e.stopPropagation(); onOpenOffice(item.raw.office_id, item.raw.practice_id); }}
      >
        {label}
      </button>,
    );
  } else if (ctx.practice) {
    chips.push(<span key="practice" className="crmt-chip" title={ctx.practice.name}>{ctx.practice.name}</span>);
  }
  if (ctx.lead) {
    chips.push(
      <button
        key="lead"
        type="button"
        className="crmt-chip"
        title={ctx.lead.name}
        onClick={(e) => { e.stopPropagation(); onOpenLead(ctx.lead.id); }}
      >
        Lead{item.kind === "lead" || item.kind === "invite" ? "" : ` · ${ctx.lead.name}`}
      </button>,
    );
  }
  if (f.resurfaced) {
    chips.push(
      <span key="back" className="crmt-chip is-back" title={f.dismissed_at ? `Dismissed ${dayjs(f.dismissed_at).format("MMM D")}; changed since` : "Changed since it was dismissed"}>
        Back — changed since dismissed
      </span>,
    );
  }
  if (f.dormant) chips.push(<span key="dormant" className="crmd-pill">Dormant</span>);
  if (f.trial_enabled) chips.push(<span key="trial" className="crmt-chip is-soft">Free trial</span>);
  if (f.is_ftc) chips.push(<span key="ftc" className="crmt-chip is-soft">FTC{item.raw.mailbox ? ` · ${item.raw.mailbox}` : ""}</span>);
  // Which of our inboxes the clinic wrote to — with support@, sales@ and a
  // personal inbox all read, "reply" means little without it.
  else if (item.kind === "thread" && item.raw && item.raw.mailbox) {
    chips.push(<span key="inbox" className="crmt-chip is-soft">to {item.raw.mailbox}</span>);
  }
  if (item.kind === "invite" && item.raw && item.raw.receiver_email && item.title !== item.raw.receiver_email && !item.subtitle) {
    chips.push(<span key="email" className="crmt-chip is-soft">{item.raw.receiver_email}</span>);
  }
  if (item.kind === "thread" && item.raw && item.raw.message_count > 1) {
    chips.push(<span key="n" className="crmt-chip is-soft">{plural(item.raw.message_count, "message")}</span>);
  }
  return chips.length ? <div className="crmt-ctx">{chips}</div> : null;
}

function copyText(text) {
  if (!text) return Promise.reject(new Error("nothing to copy"));
  if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error("copy failed"));
    } catch (e) {
      reject(e);
    }
  });
}

function TodayRow({ item, busy, leaving, titleRef, onPrimary, onOpenOffice, onOpenLead, onAct, dismissed }) {
  const meta = KIND[item.kind] || KIND.task;
  const Icon = meta.Icon;
  const w = item.when || {};
  const sub = whenSub(item);
  const isBusy = (action) => busy === `${item.key}:${action}`;
  const anyBusy = !!busy && busy.startsWith(`${item.key}:`);
  // In the Dismissed view a row offers Restore and Open — nothing that would
  // act on a thing the team has said is not on the plate.
  const actions = dismissed ? [] : item.actions || [];
  const d = item.dismissal;

  return (
    <div className={`crmt-row${leaving ? " is-leaving" : ""}`} role="listitem" data-kind={item.kind}>
      <span className={`crmt-glyph ${item.kind}`} aria-hidden="true"><Icon /></span>
      <div className="crmt-main">
        <button ref={titleRef} type="button" className="crmt-title" title={item.title} onClick={() => onPrimary(item)}>
          {item.title || "(untitled)"}
        </button>
        {item.subtitle ? <div className="crmt-sub" title={item.subtitle}>{item.subtitle}</div> : null}
        <Chips item={item} onOpenOffice={onOpenOffice} onOpenLead={onOpenLead} />
        {dismissed && d ? (
          <div className="crmt-sub crmt-dismissed-by">
            Dismissed {d.by ? `by ${d.by.name || d.by.email} ` : ""}{dayjs(d.at).fromNow()}
          </div>
        ) : null}
      </div>
      <div className="crmt-meta">
        <span className={`crmt-when is-${w.bucket || "muted"}`}>{whenText(item)}</span>
        {sub ? <span className="crm-sub">{sub}</span> : null}
        <Who item={item} />
      </div>
      <div className="crmt-actions" onClick={(e) => e.stopPropagation()}>
        {actions.includes("done") ? (
          <Tooltip title="Mark done">
            <Button size="small" icon={<CheckOutlined />} loading={isBusy("done")} disabled={anyBusy} onClick={() => onAct(item, "done")}>
              Done
            </Button>
          </Tooltip>
        ) : null}
        {actions.includes("copy_link") ? (
          <Tooltip title="Copy invite link">
            <Button size="small" icon={<LinkOutlined />} onClick={() => onAct(item, "copy_link")} aria-label="Copy invite link" />
          </Tooltip>
        ) : null}
        {actions.includes("resend") ? (
          <Button size="small" loading={isBusy("resend")} disabled={anyBusy} onClick={() => onAct(item, "resend")}>
            Resend
          </Button>
        ) : null}
        {actions.includes("revoke") ? (
          <Popconfirm title="Revoke this invite?" okText="Revoke" okButtonProps={{ danger: true }} onConfirm={() => onAct(item, "revoke")}>
            <Button size="small" danger loading={isBusy("revoke")} disabled={anyBusy}>Revoke</Button>
          </Popconfirm>
        ) : null}
        {actions.includes("invite") ? (
          <Button size="small" icon={<MailOutlined />} onClick={() => onAct(item, "invite")}>Invite</Button>
        ) : null}
        {actions.includes("reject") ? (
          <Button size="small" loading={isBusy("reject")} disabled={anyBusy} onClick={() => onAct(item, "reject")}>Reject</Button>
        ) : null}
        {actions.includes("accept") ? (
          <Button size="small" type="primary" loading={isBusy("accept")} disabled={anyBusy} onClick={() => onAct(item, "accept")}>
            Accept
          </Button>
        ) : null}
        {actions.includes("dismiss") ? (
          <Tooltip title="Not on my plate — it comes back by itself if anything changes">
            <Button
              size="small"
              type="text"
              icon={<EyeInvisibleOutlined />}
              loading={isBusy("dismiss")}
              disabled={anyBusy}
              onClick={() => onAct(item, "dismiss")}
              aria-label="Dismiss"
            >
              Dismiss
            </Button>
          </Tooltip>
        ) : null}
        {dismissed ? (
          <Button size="small" icon={<UndoOutlined />} loading={isBusy("restore")} disabled={anyBusy} onClick={() => onAct(item, "restore")}>
            Restore
          </Button>
        ) : null}
        <Button size="small" type="text" onClick={() => onPrimary(item)} aria-label={`Open ${meta.one}`}>
          Open <ArrowRightOutlined />
        </Button>
      </div>
    </div>
  );
}

// Drop one item out of the payload and keep the counts honest.
function withoutItem(data, key) {
  if (!data) return data;
  let removed = null;
  if (data.dismissed && data.dismissed.items.some((i) => i.key === key)) {
    const items = data.dismissed.items.filter((i) => i.key !== key);
    return {
      ...data,
      dismissed: { ...data.dismissed, items, total: Math.max(0, data.dismissed.total - 1) },
      counts: { ...data.counts, dismissed: Math.max(0, (data.counts.dismissed || 0) - 1) },
    };
  }
  const sections = data.sections.map((s) => {
    const items = s.items.filter((i) => {
      if (i.key === key) { removed = i; return false; }
      return true;
    });
    if (items.length === s.items.length) return s;
    return { ...s, items, total: Math.max(0, s.total - 1) };
  });
  if (!removed) return data;
  const counts = { ...data.counts, by_kind: { ...(data.counts.by_kind || {}) } };
  const b = removed.when && removed.when.bucket;
  if (b && typeof counts[b] === "number") counts[b] = Math.max(0, counts[b] - 1);
  if (typeof counts.by_kind[removed.kind] === "number") counts.by_kind[removed.kind] = Math.max(0, counts.by_kind[removed.kind] - 1);
  counts.badge = (counts.overdue || 0) + (counts.today || 0);
  return { ...data, sections, counts };
}

function NewTaskModal({ open, onClose, onCreated }) {
  const [offices, setOffices] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [officeId, setOfficeId] = useState(null);
  const [body, setBody] = useState("");
  const [due, setDue] = useState(null);
  const [assigneeId, setAssigneeId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [o, a] = await Promise.all([getOfficesList(), getTaskAssignees()]);
      const list = Array.isArray(o.json) ? o.json : (o.json && o.json.results) || [];
      setOffices(list.map((x) => ({
        value: x.id,
        label: [x.practice_name || (x.practice && x.practice.name), x.name].filter(Boolean).join(" · ") || `Office ${x.id}`,
      })));
      setAssignees(Array.isArray(a.json) ? a.json.map((u) => ({ value: u.id, label: u.name || u.email })) : []);
    })();
  }, [open]);

  const reset = () => { setOfficeId(null); setBody(""); setDue(null); setAssigneeId(null); };

  const save = async () => {
    if (!officeId || !body.trim()) return;
    setSaving(true);
    try {
      const r = await addOfficeNote(officeId, body.trim(), {
        is_task: true, due_date: due ? due.format("YYYY-MM-DD") : null, assignee_id: assigneeId || null,
      });
      if (r.status >= 200 && r.status < 300) {
        message.success("Task added.");
        reset();
        onCreated();
        onClose();
      } else {
        message.error((r.json && r.json.detail) || "Couldn't add the task.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="New task"
      okText="Add task"
      onOk={save}
      onCancel={() => { reset(); onClose(); }}
      confirmLoading={saving}
      okButtonProps={{ disabled: !officeId || !body.trim() }}
      destroyOnClose
    >
      <div style={{ display: "grid", gap: 10 }}>
        <Select
          showSearch
          placeholder="Which office is this about?"
          value={officeId}
          options={offices}
          optionFilterProp="label"
          onChange={setOfficeId}
        />
        <Input.TextArea
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder="What needs doing? (this is also the note on the office)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <DatePicker value={due} onChange={setDue} placeholder="Due date (optional)" style={{ flex: 1 }} />
          <Select
            allowClear
            placeholder="Assign to (optional)"
            value={assigneeId}
            options={assignees}
            onChange={setAssigneeId}
            style={{ flex: 1 }}
          />
        </div>
      </div>
    </Modal>
  );
}

export default function CrmToday({
  reloadTick, onCount, onOpenOffice, onOpenLead, onGoToInvites, onInviteLead, onInviteCustomer,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scope, setScope] = useState(readScope);
  const [kind, setKind] = useState("all");
  const [busy, setBusy] = useState("");
  const [leaving, setLeaving] = useState(() => new Set());
  const [showAllUnscheduled, setShowAllUnscheduled] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const dataRef = useRef(null);
  const loadedOnce = useRef(false);
  const titleRefs = useRef({});
  dataRef.current = data;

  // The parent's callback is read through a ref, never as a dependency. `report`
  // feeds `load`, and `load` feeds the fetch effect — so a parent passing an
  // inline function made every one of its renders refetch, and each fetch
  // re-rendered the parent: ~100 requests a minute, and a page that re-rendered
  // twice a second under whatever the user was typing into.
  const onCountRef = useRef(onCount);
  onCountRef.current = onCount;
  const report = useCallback((d) => {
    const cb = onCountRef.current;
    if (cb && d && d.counts) cb(d.counts.badge || 0, d.counts);
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent || loadedOnce.current) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await getToday({ date: localDay(), scope });
      if (res.status >= 200 && res.status < 300 && res.json) {
        setData(res.json);
        report(res.json);
      } else if (res.status !== 403) {
        message.error((res.json && res.json.detail) || "Couldn't load Today.");
      }
    } finally {
      loadedOnce.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [scope, report]);

  useEffect(() => { load(); }, [load, reloadTick]);

  const changeScope = (v) => {
    writeScope(v);
    setScope(v);
  };

  // Optimistic removal with rollback: the row leaves immediately, the request
  // follows, and a failure puts the exact previous payload back.
  const removeThen = async (item, action, request, okText, failText) => {
    const snapshot = dataRef.current;
    setBusy(`${item.key}:${action}`);
    // Where focus goes once this row is gone: the next title in the same section.
    let nextKey = null;
    if (snapshot) {
      for (const s of snapshot.sections) {
        const idx = s.items.findIndex((i) => i.key === item.key);
        if (idx >= 0) { nextKey = (s.items[idx + 1] || s.items[idx - 1] || {}).key || null; break; }
      }
    }
    setLeaving((prev) => new Set(prev).add(item.key));
    const timer = setTimeout(() => {
      setData((d) => {
        const next = withoutItem(d, item.key);
        report(next);
        return next;
      });
      setLeaving((prev) => { const n = new Set(prev); n.delete(item.key); return n; });
      if (nextKey && titleRefs.current[nextKey]) titleRefs.current[nextKey].focus();
    }, 180);
    try {
      const res = await request();
      if (res.status >= 200 && res.status < 300) {
        message.success(okText);
        load({ silent: true });
      } else {
        clearTimeout(timer);
        setLeaving((prev) => { const n = new Set(prev); n.delete(item.key); return n; });
        setData(snapshot);
        report(snapshot);
        message.error((res.json && res.json.detail) || failText);
      }
    } finally {
      setBusy("");
    }
  };

  const openPrimary = (item) => {
    const raw = item.raw || {};
    if (item.kind === "task") return onOpenOffice(raw.office_id, raw.practice_id, "notes");
    if (item.kind === "invite") return onGoToInvites(raw.receiver_email);
    if (item.kind === "thread") {
      // Straight to the email waiting for an answer, in the office's or the lead's list.
      if (raw.office_id) return onOpenOffice(raw.office_id, raw.practice_id, "conversation", raw.message_id);
      if (raw.lead_id) return onOpenLead(raw.lead_id, raw.message_id);
      return null;
    }
    if (item.kind === "lead") return onOpenLead(item.id);
    if (item.kind === "request") return message.info("Accept or reject it right here, or review it under Carrier Knowledge → Requests.");
    return null;
  };

  const act = async (item, action) => {
    const raw = item.raw || {};
    if (action === "done") {
      return removeThen(item, action, () => updateOfficeNote(raw.office_id, item.id, { completed: true }),
        "Done.", "Couldn't complete the task.");
    }
    if (action === "revoke") {
      return removeThen(item, action, () => revokeInvite(item.id), "Invite revoked.", "Couldn't revoke the invite.");
    }
    if (action === "accept" || action === "reject") {
      return removeThen(item, action, () => reviewPlatformRequest(item.id, action),
        action === "accept" ? "Applied." : "Rejected.", `Couldn't ${action} the request.`);
    }
    if (action === "dismiss") {
      return removeThen(item, action, () => dismissTodayItem(item.kind, item.id, raw.version),
        "Dismissed. It comes back by itself if anything changes.", "Couldn't dismiss it.");
    }
    if (action === "restore") {
      return removeThen(item, action, () => restoreTodayItem(item.kind, item.id),
        "Back on the plate.", "Couldn't restore it.");
    }
    if (action === "resend") {
      setBusy(`${item.key}:resend`);
      try {
        const r = await resendInvite(item.id);
        if (r.status >= 200 && r.status < 300) {
          message.success(r.json && r.json.email_sent === false ? "Invite renewed (email not sent)." : "Invite resent.");
          load({ silent: true });
        } else {
          message.error((r.json && r.json.detail) || "Couldn't resend the invite.");
        }
      } finally {
        setBusy("");
      }
      return null;
    }
    if (action === "copy_link") {
      try {
        await copyText(raw.invite_link);
        message.success("Invite link copied.");
      } catch (e) {
        if (raw.invite_link) message.warning(raw.invite_link, 6);
        else message.error("This invite has no link.");
      }
      return null;
    }
    if (action === "invite") return onInviteLead(item);
    return null;
  };

  const day = (data && data.date) || localDay();
  const counts = (data && data.counts) || {};
  const byKind = counts.by_kind || {};
  const errors = (data && data.errors) || [];

  const summaryParts = useMemo(() => {
    const parts = [];
    if (counts.overdue) parts.push({ text: plural(counts.overdue, "overdue", "overdue"), cls: "is-danger" });
    if (counts.today) parts.push({ text: `${counts.today} due today`, cls: "is-warn" });
    if (byKind.invite) parts.push({ text: `${plural(byKind.invite, "invite")} pending` });
    if (byKind.thread) parts.push({ text: `${plural(byKind.thread, "email")} awaiting a reply` });
    if (byKind.lead) parts.push({ text: `${plural(byKind.lead, "new lead")} untouched` });
    if (byKind.request) parts.push({ text: `${plural(byKind.request, "request")} to review` });
    if (!parts.length && counts.upcoming) parts.push({ text: `${plural(counts.upcoming, "thing")} coming up, nothing due` });
    return parts;
  }, [counts.overdue, counts.today, counts.upcoming, byKind.invite, byKind.thread, byKind.lead, byKind.request]);

  const visibleSections = useMemo(() => {
    if (!data) return [];
    return data.sections.map((s) => ({
      ...s,
      items: kind === "all" ? s.items : s.items.filter((i) => i.kind === kind),
    }));
  }, [data, kind]);
  const nothingAtAll = data && data.sections.every((s) => s.items.length === 0);
  const nothingVisible = data && visibleSections.every((s) => s.items.length === 0);

  return (
    <div className="crmt">
      <div className="crmt-head">
        <div className="crmt-summary">
          <div className="crmt-summary-day">{dayjs(day).format("dddd, MMMM D")}</div>
          <div className="crmt-summary-line" aria-live="polite">
            {loading && !data ? "Gathering your plate…" : null}
            {data && summaryParts.length === 0 ? "Nothing on your plate." : null}
            {data && summaryParts.map((p, i) => (
              <React.Fragment key={p.text}>
                {i > 0 ? <span className="crmt-dot">·</span> : null}
                <span className={p.cls || ""}>{p.text}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="crmt-controls">
          {data && data.ftc && data.ftc.total ? (
            <a className="crmt-ftc" href="#/ftc-users" title="Open the FTC dashboard">
              FTC team <b>{data.ftc.total}</b> outstanding <ArrowRightOutlined />
            </a>
          ) : null}
          <Segmented
            value={scope}
            onChange={changeScope}
            options={[{ label: "Mine", value: "me" }, { label: "Everyone", value: "all" }]}
          />
          <Button icon={<PlusOutlined />} onClick={() => setNewTaskOpen(true)}>New task</Button>
          {refreshing ? <span className="crmt-refreshing">Refreshing…</span> : null}
        </div>
      </div>

      {errors.length ? (
        <div className="crmt-errors" role="status">
          <span>
            Couldn't load {errors.map((e) => ({ tasks: "tasks", invites: "invites", threads: "emails", leads: "leads", requests: "requests", ftc: "the FTC count" }[e] || e)).join(", ")} — the rest is up to date.
          </span>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => load({ silent: true })}>Retry</Button>
        </div>
      ) : null}

      {data ? (
        <div className="crmt-filters" role="tablist" aria-label="Filter by kind">
          <button
            type="button"
            role="tab"
            aria-selected={kind === "all"}
            className={`crmt-filter${kind === "all" ? " is-active" : ""}`}
            onClick={() => setKind("all")}
          >
            All <span className="crmt-filter-n">{KIND_ORDER.reduce((n, k) => n + (byKind[k] || 0), 0)}</span>
          </button>
          {KIND_ORDER.map((k) => {
            const n = byKind[k] || 0;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                className={`crmt-filter${kind === k ? " is-active" : ""}${n === 0 ? " is-empty" : ""}`}
                onClick={() => { setKind(kind === k ? "all" : k); setShowDismissed(false); }}
              >
                {KIND[k].label} <span className="crmt-filter-n">{n}</span>
              </button>
            );
          })}
          <button
            type="button"
            className={`crmt-filter is-ghost${showDismissed ? " is-active" : ""}${!(counts.dismissed) ? " is-empty" : ""}`}
            aria-pressed={showDismissed}
            title="Items the team took off the plate; each comes back by itself when it changes"
            onClick={() => setShowDismissed((v) => !v)}
            disabled={!counts.dismissed && !showDismissed}
          >
            <EyeInvisibleOutlined /> Dismissed <span className="crmt-filter-n">{counts.dismissed || 0}</span>
          </button>
        </div>
      ) : null}

      {data && showDismissed ? (
        <section className="ftcd-sec crmt-sec is-dismissed" aria-labelledby="crmt-sec-dismissed">
          <div className="ftcd-sec-head">
            <h3 className="ftcd-sec-title" id="crmt-sec-dismissed" tabIndex={-1} style={{ margin: 0 }}>Dismissed</h3>
            <span className="crm-section-count">{data.dismissed ? data.dismissed.total : 0}</span>
            <span className="crmt-sec-sub">Off the plate for now — each comes back by itself when it changes</span>
          </div>
          <div className="crm-table-card">
            <div className="crmt-list" role="list">
              {data.dismissed && data.dismissed.items.length ? data.dismissed.items
                .filter((i) => kind === "all" || i.kind === kind)
                .map((item) => (
                  <TodayRow
                    key={item.key}
                    item={item}
                    dismissed
                    busy={busy}
                    leaving={leaving.has(item.key)}
                    titleRef={(el) => { if (el) titleRefs.current[item.key] = el; else delete titleRefs.current[item.key]; }}
                    onPrimary={openPrimary}
                    onOpenOffice={onOpenOffice}
                    onOpenLead={onOpenLead}
                    onAct={act}
                  />
                )) : (
                  <div style={{ padding: 22 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nothing dismissed." /></div>
                )}
            </div>
          </div>
        </section>
      ) : null}

      {loading && !data ? (
        <div className="crm-table-card"><div style={{ padding: 32 }}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Loading…" /></div></div>
      ) : null}

      {data && !showDismissed && nothingAtAll ? (
        <div className="crm-table-card crmt-clear">
          <div className="crmt-clear-mark"><SmileOutlined /></div>
          <div className="crmt-clear-title">You're clear.</div>
          <div className="crmt-clear-sub">
            {scope === "me" ? "Nothing overdue, due or waiting that's yours or unassigned." : "Nothing overdue, due or waiting on anyone."}
          </div>
          <div className="crmt-clear-actions">
            <Button icon={<PlusOutlined />} onClick={() => setNewTaskOpen(true)}>New task</Button>
            <Button type="primary" icon={<MailOutlined />} onClick={onInviteCustomer}>Invite customer</Button>
          </div>
        </div>
      ) : null}

      {data && !showDismissed && !nothingAtAll && nothingVisible ? (
        <div className="crm-table-card"><div style={{ padding: 28 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${KIND[kind] ? KIND[kind].label.toLowerCase() : "items"} right now.`} />
        </div></div>
      ) : null}

      {data && !showDismissed && !nothingVisible ? visibleSections.map((s) => {
        if (s.items.length === 0) return null;
        const folded = s.key === "unscheduled" && !showAllUnscheduled && s.items.length > FOLD_UNSCHEDULED_AT;
        const items = folded ? s.items.slice(0, FOLD_UNSCHEDULED_AT) : s.items;
        const shownTotal = kind === "all" ? s.total : s.items.length;
        return (
          <section key={s.key} className={`ftcd-sec crmt-sec is-${s.key}`} aria-labelledby={`crmt-sec-${s.key}`}>
            <div className="ftcd-sec-head">
              <h3 className="ftcd-sec-title" id={`crmt-sec-${s.key}`} tabIndex={-1} style={{ margin: 0 }}>{s.title}</h3>
              <span className="crm-section-count">{shownTotal}</span>
              {SECTION_SUB[s.key] ? <span className="crmt-sec-sub">{SECTION_SUB[s.key]}</span> : null}
            </div>
            <div className="crm-table-card">
              <div className="crmt-list" role="list">
                {items.map((item) => (
                  <TodayRow
                    key={item.key}
                    item={item}
                    busy={busy}
                    leaving={leaving.has(item.key)}
                    titleRef={(el) => { if (el) titleRefs.current[item.key] = el; else delete titleRefs.current[item.key]; }}
                    onPrimary={openPrimary}
                    onOpenOffice={onOpenOffice}
                    onOpenLead={onOpenLead}
                    onAct={act}
                  />
                ))}
                {folded ? (
                  <button type="button" className="crmt-more" onClick={() => setShowAllUnscheduled(true)}>
                    Show all {s.items.length}
                  </button>
                ) : null}
                {s.truncated ? (
                  <div className="crmt-truncated">Showing {s.items.length} of {s.total} — narrow it down with the filters above.</div>
                ) : null}
              </div>
            </div>
          </section>
        );
      }) : null}

      <NewTaskModal open={newTaskOpen} onClose={() => setNewTaskOpen(false)} onCreated={() => load({ silent: true })} />
    </div>
  );
}
